<script>
import parseDate from 'date-fns/parse'
import lazyLoadComponent from '@router/lazyload-component'
import { couponComputed } from '@state/helpers'

import Layout from '@layouts/main'
import BaseButtonDialog from '@components/BaseButtonDialog'

import { generateUniqueString } from '@utils'
import { defaultStatusFilter, numberUnitFilter } from '@utils/filter-value-to-text'
import { formatDateRange } from '@utils/format-date'
import pageMixin from '@src/mixins/pagination'
import { messageStore, messageDelete } from '@utils/message'
import { messageBoxDelete } from '@utils/message-box'

export default {
  page: {
    title: '優惠券設定',
    meta: [{ name: 'description', content: 'Coupon List' }],
  },
  components: {
    Layout,
    BaseButtonDialog,
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
  },
  filters: {
    defaultStatusFilter,
    numberUnitFilter,
    formatDateRange,
  },
  mixins: [pageMixin],
  data() {
    return {
      loadedCoupons: [],
      selectedCoupons: [],
      filterData: {
        state: '',
        startDate: '',
        endDate: '',
        type: '',
        inProgress: '',
      },
      isFilterChange: false,
      action: ''
    }
  },
  computed: {
    ...couponComputed,
    enabledBulkButton() {
      return this.selectedCoupons.length === 0
    },
    enabledCopyButton() {
      return this.selectedCoupons.length === 1
    },
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || Array.from({ length: 2 }, () => '')
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
    disabledAction() {
      return this.selectedCoupons.length === 0
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToSearchCoupon',
      immediate: true,
    },
    filterData: {
      handler() {
        this.isFilterChange = true
      },
      deep: true,
    },
  },
  methods: {
    async tryToSearchCouponFilter() {
      // 當篩選前不在第一頁的時候，跳回第一頁
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }
      const { items, total } = await this.$store.dispatch('coupon/fetchList', Object.assign(this.filterData, this.pageQuery))
      this.loadedCoupons = items
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
      })
      this.isFilterChange = false
    },
    currentPageLimit(){
      const remainder = this.$refs.pagination.total % this.$refs.pagination.pageSize
      const qotient = this.$refs.pagination.total / this.$refs.pagination.pageSize
      const totalPage = remainder ? parseInt(qotient) + 1 : parseInt(qotient)
      if (this.currentPage > totalPage) {
        this.currentPage = totalPage
      }
    },
    async tryToSearchCoupon() {
      const { items, total } = await this.$store.dispatch('coupon/fetchList', Object.assign(this.filterData, this.pageQuery))
      this.loadedCoupons = items
      this.$nextTick(() => {
        this.$refs.pagination && this.$refs.pagination.updateTotal(total)
        this.currentPageLimit()
      })
    },
    handleResetSearch() {
      this.filterDateRange = ['', '']
      this.tryToSearchCoupon()
    },
    checkAvailability(row) {
      const now = new Date()
      const { state, startDate, endDate } = row
      if (state === 0) return false

      if (startDate && endDate) return now >= parseDate(startDate) && now <= parseDate(endDate)
      if (!startDate) return now <= parseDate(endDate)
      if (!endDate) return now >= parseDate(startDate)
      return state
    },
    goToViewCoupon(row) {
      const couponId = row.id
      this.$router.push({ name: 'view-coupon', params: { couponId } })
    },
    goToAddCoupon(data) {
      this.$router.push({
        name: 'add-coupon',
        params: {
          data,
        },
      })
    },
    // async handleBulkUpdateState(state) {
    //   const couponIds = this.selectedCoupons.map(({ id }) => id)
    //   await this.$store.dispatch('coupon/updateState', {
    //     ids: couponIds,
    //     state,
    //   })
    //   this.tryToSearchCoupon()
    // },
    async handleStoreMemberLevel(state) {
      if (state === -1) {
        this.handleDeleteCoupon()
      } else {
        try {
          const couponIds = this.selectedCoupons
          await this.$store.dispatch('coupon/updateState', {
            ids: couponIds,
            state,
          })
          if (state === -1)
            messageDelete()
          this.action = ''
          this.tryToSearchCoupon()
        } finally {

        }
      }
    },
    onSelectAction(action) {
      this.handleStoreMemberLevel(action)
    },
    async handleDeleteCoupon() {
      try {
        await messageBoxDelete('優惠券')
        try {
          const couponIds = this.selectedCoupons
          await this.$store.dispatch('coupon/remove', { ids: couponIds })
          messageDelete()
          this.tryToSearchCoupon()
        } finally {
  
        }
      } catch {

      }
  
    },
    async handleDuplicateCoupon() {
      const [couponId] = this.selectedCoupons

      const { id, title, code, ...couponData } = await this.$store.dispatch('coupon/fetchSingle', couponId)
      const randomCode = `${this.currentUser.merchantAlias}_${generateUniqueString(5)}`

      const duplicateData = {
        ...couponData,
        title: `${title}（複製）`,
        code: randomCode.toUpperCase(),
      }
      this.goToAddCoupon(duplicateData)

      // await this.$store.dispatch('coupon/store', duplicateData)
      // this.tryToSearchCoupon()
    },
    handleSelectionChange(selected) {
      this.selectedCoupons = selected.map(({ id }) => id)
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.pageContent">
      <div :class="$style.btnTop" class="btn-top-box">
        <div class="d-sm-flex justify-content-end">
          <span>
            <el-button class="is-round" type="primary" @click="goToAddCoupon({})">
              <BaseIcon name="plus"/>
              <span>新增優惠卷</span>
            </el-button>
          </span>
          <span class="ml-3">
            <el-select 
              v-model="action" 
              placeholder="批次執行"
              :disabled="disabledAction" 
              class="is-border" 
              @change="onSelectAction"
            >
              <el-option label="立即開放" :value="1"></el-option>
              <el-option label="取消開放" :value="0"></el-option>
              <el-option label="刪除選取" :value="-1"></el-option>
            </el-select>
          </span>
          <span class="ml-3">
            <el-button class="is-round" :disabled="!enabledCopyButton" @click="handleDuplicateCoupon">
              <BaseIcon name="copy" />
              <span>複製</span>
            </el-button>
          </span>
        </div>
      </div>
      <BaseTableSearch 
        v-slot="{ fields }" 
        :default-value="filterData"
        hide-action 
        @reset="handleResetSearch"
        @tryToFetchList="tryToSearchCouponFilter"
      >
        <el-form-item prop="state">
          <el-select v-model="fields.state" placeholder="開放狀態" @change="tryToSearchCouponFilter">
            <el-option label="開放中" value="1"></el-option>
            <el-option label="未開放" value="0"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="inProgress">
          <el-select v-model="fields.inProgress" placeholder="使用狀態" @change="tryToSearchCouponFilter">
            <el-option label="可使用" value="1"></el-option>
            <el-option label="不可使用" value="0"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="type">
          <el-select v-model="fields.type" placeholder="折扣類型" @change="tryToSearchCouponFilter">
            <el-option label="固定金額（元）" value="fixed"></el-option>
            <el-option label="按訂單總額比例（％）" value="%"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-date-picker
            v-model="filterDateRange"
            type="daterange"
            align="right"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="起始日期"
            end-placeholder="結束日期"
            :clearable="false"
            unlink-panels
            @change="tryToSearchCouponFilter"
          ></el-date-picker>
        </el-form-item>
      </BaseTableSearch>
      <div class="isStylePageContent pt-0">
        <div class="el-table-wrapper">
          <el-table class="Jost" :data="loadedCoupons" :class="$style['table']" @row-click.once="goToViewCoupon" @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="80"></el-table-column>
            <el-table-column v-slot="{ row }" label="優惠券名稱跟代號" min-width="150">
              <b>{{ row.title }}</b>
              <div class="fs-sm text-secondary-light">{{ row.code }}</div>
            </el-table-column>
            <el-table-column v-slot="{ row }" label="使用狀態" min-width="100" align="center">
              <BaseIcon v-if="checkAvailability(row)" name="check" class="text-success" />
              <BaseIcon v-else name="minus" icon-style="fal" class="text-secondary"/>
            </el-table-column>
            <el-table-column v-slot="{ row }" label="開放狀態與使用期間" min-width="200">
              <BaseStateLabel :state="row.state | defaultStatusFilter('color')" text>{{ row.state | defaultStatusFilter('text') }}</BaseStateLabel>
              <div class="color-text-primary">{{ row.startDate | formatDateRange(row.endDate) }}</div>
            </el-table-column>
            <el-table-column v-slot="{ row }" label="折扣" min-width="80">-{{ row.discount }}{{ row.type | numberUnitFilter }}</el-table-column>
            <el-table-column v-slot="{ row }" label="已被使用／發放總張數" min-width="180">{{ `${row.used}張／${row.infinity === 1 ? '無上限' : `${row.limit}張`}` }}</el-table-column>
            <el-table-column min-width="40">
              <el-tooltip content="查看優惠券詳情" placement="top">
                <el-button type="text">
                  <BaseIcon name="ellipsis-v" />
                </el-button>
              </el-tooltip>
            </el-table-column>
          </el-table>
        </div>
        <BasePagination ref="pagination" :current-page.sync="currentPage" />
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.table {
  @include layer-elevation(5);
}

.btnTop {
  padding: 0.5em 0;
  margin-bottom: 10px;
  @include max-sm {
    > * {
      &:not(:first-child) {
        width: 50%;
      }
    }
  }
}
</style>
